import { Result, backendApi } from "./backendApi";
import { UserModel } from "./userAPI";

export enum VehicleTypeModel {
  Car = 1,
  Truck = 2,
  Bus = 3,
  Special = 4,
  Camper = 5,
  Motorcycle = 6,
  Agricultural = 7,
  Construction = 8,
  Vessel = 9,
  Trailer = 10,
  EnergyStorageSystems = 11,
}

export function getVehicleTypeString(type: VehicleTypeModel) {
  switch (type) {
    case VehicleTypeModel.EnergyStorageSystems:
      return "Energy Storage System";
    default:
      return VehicleTypeModel[type];
  }
}

export interface VehicleFileModel {
  description: string;
  type: VehicleTypeModel;
  rsa: boolean;
}

export interface VehicleFileViewModel {
  number: string;
  make: string;
  description: string | null;
  checkedOutBy: UserModel | null;
  helperFilesValidated: boolean;
  crsFileValidated: boolean;
  type: VehicleTypeModel;
  rsa: boolean;
}

export interface VehicleFileDetailsViewModel {
  number: string;
  make: string;
  description: string | null;
  checkedOutBy: UserModel | null;
  crsFiles: DataFileModel[];
  pdfFiles: DataFileModel[];
  autocadFiles: DataFileModel[];
  selectedFilesCrsPic: DataFileModel[];
  selectedFilesTechInfo: DataFileModel[];
  sourceFilesOrgPic: DataFileModel[];
  sourceFilesTechInfoEU: DataFileModel[];
  sourceFilesTechInfoUSA: DataFileModel[];
  sourceFilesTechInfoPacific: DataFileModel[];
  sourceFilesArchive: DataFileModel[];
  threeDFiles: DataFileModel[];
  qrCodeFiles: DataFileModel[];
  oemRescueSheetFiles: DataFileModel[];
  helperFilesValidated: boolean;
  crsFileValidated: boolean;
  modelInfoValidated: boolean;
  type: VehicleTypeModel;
  rsa: boolean;
}

export enum DataFileTypeModel {
  CrsFile = 1,
  PDFFile = 2,
  AutoCADFile = 3,
  SelectedFileCrsPic = 4,
  SourceFileOrgPic = 5,
  ThreeDFile = 6,
  QRCodeFile = 7,
  OEMRescueSheetFile = 8,
  SourceFileTechInfoEU = 9,
  SourceFileTechInfoUSA = 10,
  SourceFileTechInfoPacific = 11,
  SelectedFileCheckInfo = 12,
  SourceFileArchive = 13,
}

export enum VehicleFileValidationTypeModel {
  CrsFile = 1,
  ModelInfo = 2,
  HelperFilesValidation = 3,
}

export interface DataFileModel {
  type: DataFileTypeModel;
  filename: string;
  updatedBy?: string;
  updatedDate?: string;
  deleted: boolean;
  version?: number;
}

export interface ChangeSetDataFileModel {
  fileName: string;
  type: DataFileTypeModel;
  version: number;
  deleted: boolean;
}

export interface ChangeSetVehicleModel {
  recId: number;
  version: number;
  deleted: boolean;
}

export interface ChangeSetModel {
  timestamp: string;
  message: string;
  userName: string;
  originalUserName?: string;
  dataFiles: ChangeSetDataFileModel[];
  vehicles: ChangeSetVehicleModel[];
}

class GetVehicleFilesByMakeRequest {}

class GetVehicleFilesByMakeResponse {
  vehicleFiles: VehicleFileViewModel[] = [];
}

class CreateVehicleFileRequest {
  makeCode?: string;
  vehicleFile?: VehicleFileModel;
}

class UpdateVehicleFileRequest {
  number?: string;
  vehicleFile?: VehicleFileModel;
}

class CreateVehicleFileResponse {
  vehicleFileNumber?: string;
}

class GetVehicleFileDetailsRequest {
  number?: string;
}

class GetVehicleFileDetailsResponse {
  vehicleFile!: VehicleFileDetailsViewModel;
}

class GetCheckedOutVehicleFilesByUserRequest {}

class GetCheckedOutVehicleFilesByUserResponse {
  vehicleFiles: VehicleFileViewModel[] = [];
}

class CheckinVehicleFileRequest {
  number!: string;
  message!: string;
  force!: boolean;
}

class CheckoutVehicleFileRequest {
  number!: string;
}

class RemoveDataFileRequest {
  vehicleFileNumber!: string;
  filename!: string;
}

class RemoveRawDataFileRequest {
  vehicleFileNumber!: string;
  filename!: string;
}

class ValidateVehicleFileRequest {
  vehicleFileNumber!: string;
  type!: VehicleFileValidationTypeModel;
  validated!: boolean;
}

class CheckForChangesRequest {
  number!: string;
}

class CheckForChangesResponse {
  hasChanges!: boolean;
}

class UndoCheckOutRequest {
  number!: string;
  force!: boolean;
}

class GetVersionHistoryRequest {
  number!: string;
}

class GetVersionHistoryResponse {
  checkedOutBy?: string;
  currentFiles!: ChangeSetDataFileModel[];
  currentVehicles!: ChangeSetVehicleModel[];
  changeSets!: ChangeSetModel[];
}

function isRawDataFileType(type: DataFileTypeModel) {
  if (
    type === DataFileTypeModel.SourceFileOrgPic ||
    type === DataFileTypeModel.SourceFileTechInfoEU ||
    type === DataFileTypeModel.SourceFileTechInfoUSA ||
    type === DataFileTypeModel.SourceFileTechInfoPacific ||
    type === DataFileTypeModel.SourceFileArchive
  )
    return true;

  return false;
}

export function getDataFileTypeLabel(type: DataFileTypeModel): string {
  switch (type) {
    case DataFileTypeModel.CrsFile:
      return "MPFZ File";
    case DataFileTypeModel.PDFFile:
      return "PDF Files";
    case DataFileTypeModel.AutoCADFile:
      return "AutoCAD File";
    case DataFileTypeModel.SelectedFileCrsPic:
      return "CRS Pics";
    case DataFileTypeModel.SourceFileOrgPic:
      return "ORG Pics";
    case DataFileTypeModel.SourceFileArchive:
      return "Archive";
    case DataFileTypeModel.ThreeDFile:
      return "3D Data Files";
    case DataFileTypeModel.QRCodeFile:
      return "QR Code Files";
    case DataFileTypeModel.OEMRescueSheetFile:
      return "OEM Rescue Sheets";
    case DataFileTypeModel.SourceFileTechInfoEU:
      return "Tech info EU";
    case DataFileTypeModel.SourceFileTechInfoUSA:
      return "Tech info USA";
    case DataFileTypeModel.SourceFileTechInfoPacific:
      return "Tech info PAC";
    case DataFileTypeModel.SelectedFileCheckInfo:
      return "Check info";
    default:
      return "unknown";
  }
}

export interface VehicleFileSearchResultModel {
  number: string;
}

class SearchVehicleFilesRequest {
  constructor(
    public numberFilter: string | undefined,
    public onlyWithMpfz: boolean | undefined,
    public maxResponseCount: number,
  ) {}
}

interface SearchVehicleFilesResponse {
  vehicleFiles: VehicleFileSearchResultModel[];
}

export const vehicleFileAPI = {
  async searchVehicleFiles(
    numberFilter: string | undefined,
    onlyWithMpfz: boolean | undefined,
    maxResponseCount: number,
  ): Promise<Result<VehicleFileSearchResultModel[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<SearchVehicleFilesResponse>(
        "SearchVehicleFiles",
        new SearchVehicleFilesRequest(
          numberFilter,
          onlyWithMpfz,
          maxResponseCount,
        ),
      );

    if (response.ok) {
      return { ok: true, value: response.value.vehicleFiles };
    }

    return response;
  },

  async getVehicleFileHistory(
    number: string,
  ): Promise<Result<GetVersionHistoryResponse>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetVersionHistoryResponse>(
        "GetVersionHistory",
        { number: number } as GetVersionHistoryRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value };
    }

    return response;
  },

  async getVehicleFilesByMake(
    makeCode: string,
  ): Promise<Result<VehicleFileViewModel[]>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetVehicleFilesByMakeResponse>(
        "GetVehicleFilesByMake",
        { makeCode: makeCode } as GetVehicleFilesByMakeRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.vehicleFiles };
    }

    return response;
  },

  async getCheckedOutVehicleFilesByUser(): Promise<
    Result<VehicleFileViewModel[]>
  > {
    let response =
      await backendApi.sendAndGetResponseAsync<GetCheckedOutVehicleFilesByUserResponse>(
        "GetCheckedOutVehicleFilesByUser",
        new GetCheckedOutVehicleFilesByUserRequest(),
      );

    if (response.ok) {
      return { ok: true, value: response.value.vehicleFiles };
    }

    return response;
  },

  async createNewVehicleFile(
    makeCode: string,
    vehicleFile: VehicleFileModel,
  ): Promise<Result<string>> {
    let response =
      await backendApi.sendAndGetResponseAsync<CreateVehicleFileResponse>(
        "CreateVehicleFile",
        { makeCode, vehicleFile: vehicleFile } as CreateVehicleFileRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.vehicleFileNumber ?? "" };
    }

    return response;
  },

  async updateVehicleFile(
    number: string,
    vehicleFile: VehicleFileModel,
  ): Promise<Result<any>> {
    let response = await backendApi.sendAsync("UpdateVehicleFile", {
      number: number,
      vehicleFile: vehicleFile,
    } as UpdateVehicleFileRequest);

    if (response.ok) {
      return { ok: true, value: response.value.vehicleFileNumber ?? "" };
    }

    return response;
  },

  async getVehicleFileDetails(
    number: string,
  ): Promise<Result<VehicleFileDetailsViewModel>> {
    let response =
      await backendApi.sendAndGetResponseAsync<GetVehicleFileDetailsResponse>(
        "GetVehicleFileDetails",
        { number: number } as GetVehicleFileDetailsRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.vehicleFile! };
    }

    return response;
  },

  async undoCheckout(number: string, force: boolean): Promise<Result<any>> {
    const response = await backendApi.sendAsync("UndoCheckout", {
      number: number,
      force: force,
    } as UndoCheckOutRequest);

    if (response.ok) {
      return { ok: true, value: true };
    }

    return response;
  },

  async checkForChanges(number: string): Promise<Result<boolean>> {
    let response =
      await backendApi.sendAndGetResponseAsync<CheckForChangesResponse>(
        "CheckForChanges",
        {
          number: number,
        } as CheckForChangesRequest,
      );

    if (response.ok) {
      return { ok: true, value: response.value.hasChanges };
    }

    return { ok: false, problem: response.problem };
  },

  async checkinVehicleFile(
    number: string,
    message: string,
    force: boolean,
  ): Promise<Result<any>> {
    let response = await backendApi.sendAsync("CheckinVehicleFile", {
      number: number,
      message: message,
      force: force,
    } as CheckinVehicleFileRequest);

    if (response.ok) {
      return { ok: true, value: true };
    }

    return response;
  },

  async checkoutVehicleFile(number: string): Promise<Result<any>> {
    let response = await backendApi.sendAsync("CheckoutVehicleFile", {
      number: number,
    } as CheckoutVehicleFileRequest);

    if (response.ok) {
      return { ok: true, value: true };
    }

    return response;
  },

  async uploadDataFile(
    vehicleFileNumber: string,
    type: DataFileTypeModel,
    filename: string,
    file: File,
  ): Promise<Result<any>> {
    let url = isRawDataFileType(type) ? "rawdatafile" : "datafile";

    return await backendApi.uploadFileAsync(
      `${url}?vehicleFileNumber=${vehicleFileNumber}&type=${type}`,
      file,
      filename,
    );
  },

  async downloadDataFile(
    vehicleFileNumber: string,
    filename: string,
    type: DataFileTypeModel,
    showInline: boolean,
  ): Promise<Result<any>> {
    let url = isRawDataFileType(type) ? "rawdatafile" : "datafile";

    let response = await backendApi.downloadFileAsync(
      `${url}?vehicleFileNumber=${vehicleFileNumber}&filename=${encodeURIComponent(
        filename,
      )}&type=${type}&inline=${showInline}`,
    );

    return response;
  },

  downloadDataFileAsBlob(
    vehicleFileNumber: string,
    fileName: string,
    type: DataFileTypeModel,
    showInline: boolean,
  ) {
    let url = isRawDataFileType(type) ? "rawdatafile" : "datafile";
    let url2 = `${url}?vehicleFileNumber=${vehicleFileNumber}&filename=${fileName}&type=${type}&inline=${showInline}`;
    const mediaType = fileName.endsWith(".pdf") ? "application/pdf" : undefined;
    return backendApi.downloadBlobFile(url2, {
      MediaType: mediaType,
      FileName: fileName,
    });
  },

  async removeDataFile(
    vehicleFileNumber: string,
    filename: string,
    type: DataFileTypeModel,
  ): Promise<Result<any>> {
    if (isRawDataFileType(type)) {
      return await backendApi.sendAsync("RemoveRawDataFile", {
        vehicleFileNumber: vehicleFileNumber,
        filename: filename,
        type: type,
      } as RemoveRawDataFileRequest);
    } else {
      return await backendApi.sendAsync("RemoveDataFile", {
        vehicleFileNumber: vehicleFileNumber,
        filename: filename,
        type: type,
      } as RemoveDataFileRequest);
    }
  },

  async setVehicleValidatedFlag(
    number: string,
    type: VehicleFileValidationTypeModel,
    validated: boolean,
  ): Promise<Result<any>> {
    let response = await backendApi.sendAsync("ValidateVehicleFile", {
      vehicleFileNumber: number,
      type: type,
      validated: validated,
    } as ValidateVehicleFileRequest);

    if (response.ok) {
      return { ok: true, value: true };
    }

    return response;
  },
};
